import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { CaretDown } from "@phosphor-icons/react";

const faqs = [
  { q: "What exactly do we get?", a: "A fully branded prediction competition for your club. This includes your logo, club colours, Stripe payment integration (members pay directly into your club's bank account), automated confirmation emails, a live leaderboard, and GDPR-compliant T&Cs — all ready to share with your members." },
  { q: "How do members pay to enter?", a: "Members pay via Stripe on your branded page. You set the entry fee (£5, £10, €15 — whatever you choose). Payments go directly to your club's bank account via Stripe Connect. We never touch the money." },
  { q: "What are the ongoing costs?", a: "None. The setup fee is a one-time payment. No monthly subscriptions, no annual renewals, no commissions. The only ongoing cost is Stripe's standard processing fee (1.4% + 20p per transaction), deducted automatically by Stripe." },
  { q: "How does the World Cup Predictor work?", a: "Each member picks 6 teams: 2 favourites, 2 contenders, and 2 long shots. Points are awarded based on how far their teams progress. A live leaderboard tracks scores, with a tiebreaker on total tournament goals (excluding penalties)." },
  { q: "How long does setup take?", a: "Once you've provided your club details (logo, colours, entry fee), your branded fundraiser is live within 24 hours. You'll receive a link to share with your members." },
  { q: "Can we set our own entry fee?", a: "Absolutely. You choose the entry fee — £5, £10, £20, €15, whatever works for your club. It all goes directly to your Stripe-connected bank account." },
  { q: "Is it mobile friendly?", a: "Yes. The whole experience is designed mobile-first. Members can enter, check the leaderboard, and track their teams from any device." },
  { q: "What about GDPR?", a: "Every club fundraiser comes with auto-generated Terms & Conditions and a Privacy Policy, both branded to your club. Member data is stored securely and only used for the competition." },
  { q: "Do I need a license to run this?", a: "If your club already runs a club lotto, you likely have a Small Society Lottery license (or equivalent) which will cover TargetFunder competitions. As the club, you are the legal promoter and operator of the fundraiser, so it's worth confirming with your local authority if you're unsure. TargetFunder is a technology provider only." },
];

export default function FAQ() {
  const [showFaqs, setShowFaqs] = useState(false);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-10 lg:py-14 px-6 bg-[#F8FAFA] border-t border-[#1D3557]/5"
    >
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => setShowFaqs(!showFaqs)}
          className="flex items-center gap-3 group"
          data-testid="faq-toggle"
        >
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#E63946] mb-2 text-left">
              FAQ
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none text-[#1D3557]">
              Common Questions
            </h2>
          </div>
          <CaretDown
            size={24}
            weight="bold"
            className={`text-[#1D3557]/40 group-hover:text-[#1D3557] transition-all ${showFaqs ? "rotate-180" : ""}`}
          />
        </button>

        {showFaqs && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}
            className="mt-6"
          >
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-b border-[#1D3557]/8 border-l-0 border-r-0 border-t-0"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="font-body text-sm text-left font-semibold text-[#1D3557] hover:text-[#1D3557]/70 py-5 hover:no-underline"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-sm text-[#1D3557]/55 leading-relaxed pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        )}
      </div>
    </section>
  );
}
